import { useState, useEffect } from "react";
import "@/Clinical.css";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, MapPin, Mail, Phone, Check, HeartHandshake,
} from "lucide-react";

const JANE_URL = "https://hayahealingpsychotherapy.janeapp.com/";
const HEADSHOT = "/momna-headshot.png";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.05 + i * 0.07, ease } }),
};

const Reveal = ({ i = 0, className = "", children, ...rest }) => (
  <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: "-60px" }} className={className} {...rest}>
    {children}
  </motion.div>
);

const Banner = () => (
  <div data-testid="clinical-banner" className="bg-[#2c1f20] text-white text-xs tracking-wide text-center py-2 px-4">
    Clinical / corporate design sample — <a href="/" data-testid="view-warm-link" className="underline underline-offset-2">view current warm design</a>
  </div>
);

const TopBar = () => (
  <div className="bg-[#f1ece4] border-b border-[#e5dcd3] text-xs text-[#2c1f20]/70">
    <div className="max-w-6xl mx-auto px-6 py-2 flex flex-wrap justify-between gap-2">
      <span>Virtual across Ontario · In-person in Milton &amp; Burlington</span>
      <span className="flex gap-5">
        <a href="tel:+13656172135" className="flex items-center gap-1.5 hover:text-[#2c1f20]"><Phone size={12} /> (365) 617-2135</a>
        <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-1.5 hover:text-[#2c1f20]"><Mail size={12} /> momna@hayahealingpsychotherapy.com</a>
      </span>
    </div>
  </div>
);

const Nav = () => (
  <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#e5dcd3]">
    <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <img src="/haya-logo-trimmed.png" alt="Haya Healing Psychotherapy" data-testid="cl-nav-logo" className="w-10 h-10 object-contain" />
        <div className="leading-tight">
          <p className="font-semibold text-sm tracking-wide">HAYA HEALING</p>
          <p className="text-[10px] tracking-[0.2em] text-[#2c1f20]/50 uppercase">Psychotherapy</p>
        </div>
      </div>
      <nav className="hidden md:flex items-center gap-7 text-xs font-medium tracking-[0.12em] uppercase text-[#2c1f20]/70">
        {[["Services", "#cl-services"], ["Locations", "#cl-locations"], ["About", "#cl-about"], ["FAQ", "#cl-faq"], ["Contact", "#cl-contact"]].map(([l, h]) => (
          <a key={l} href={h} className="hover:text-[#2c1f20] transition-colors">{l}</a>
        ))}
      </nav>
      <a data-testid="cl-nav-book" href={JANE_URL} target="_blank" rel="noreferrer" className="cl-btn cl-btn-navy text-xs">Book Consultation</a>
    </div>
  </header>
);

const Hero = () => (
  <section data-testid="cl-hero" className="max-w-6xl mx-auto px-6 pt-16 md:pt-24 pb-16 grid md:grid-cols-12 gap-12 items-center">
    <div className="md:col-span-7">
      <Reveal><p className="cl-label">Registered Psychotherapy · Ontario</p></Reveal>
      <Reveal i={1}>
        <h1 className="mt-4 text-4xl md:text-6xl font-semibold leading-[1.08] tracking-tight">
          Evidence-based therapy for individuals, couples, and families.
        </h1>
      </Reveal>
      <Reveal i={2}>
        <p className="mt-6 text-[#2c1f20]/70 leading-relaxed max-w-xl">
          Haya Healing Psychotherapy provides structured, compassionate care — including South Asian couples
          therapy grounded in the Gottman Method — in English and Urdu. In person at Halton Medix Walk-In Clinic
          in Milton and Burlington, and virtually across Ontario.
        </p>
      </Reveal>
      <Reveal i={3} className="mt-8 flex flex-wrap gap-4">
        <a data-testid="cl-hero-book" href={JANE_URL} target="_blank" rel="noreferrer" className="cl-btn cl-btn-primary">Book a consultation <ArrowRight size={15} /></a>
        <a data-testid="cl-hero-services" href="#cl-services" className="cl-btn cl-btn-outline">View services</a>
      </Reveal>
      <Reveal i={4} className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#2c1f20]/60">
        {["Complimentary 15-minute consultation", "Sessions in English & Urdu", "PHIPA-compliant virtual platform"].map((t) => (
          <span key={t} className="flex items-center gap-2"><Check size={14} className="text-[#6e4550]" /> {t}</span>
        ))}
      </Reveal>
    </div>
    <Reveal i={2} className="md:col-span-5">
      <div className="cl-card overflow-hidden">
        <img src={HEADSHOT} alt="Momna Sajid, Registered Psychotherapist (Qualifying)" className="w-full h-[380px] object-cover object-top" />
        <div className="p-5 border-t border-[#e5dcd3]">
          <p className="font-semibold">Momna Sajid, RP (Qualifying)</p>
          <p className="text-sm text-[#2c1f20]/60 mt-0.5">Registered Psychotherapist (Qualifying) · CRPO</p>
        </div>
      </div>
    </Reveal>
  </section>
);

const TrustBar = () => (
  <section className="border-y border-[#e5dcd3] bg-[#f1ece4]">
    <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#e5dcd3]">
      {[["CRPO", "Registered Psychotherapist (Qualifying)"], ["EN / UR", "Sessions in English & Urdu"], ["2", "Clinics — Milton & Burlington"], ["ON", "Virtual, Ontario-wide"]].map(([n, l]) => (
        <div key={l} className="py-6 px-4 text-center">
          <p className="text-xl font-semibold text-[#2c1f20]">{n}</p>
          <p className="text-xs text-[#2c1f20]/55 mt-1">{l}</p>
        </div>
      ))}
    </div>
  </section>
);

const FOCUS = [
  ["01", "Couples & Marriage Counselling", "Gottman Method-informed work on communication, conflict repair, and rebuilding emotional safety."],
  ["02", "South Asian Couples Therapy", "Culturally attuned couples care — in-law dynamics, intergenerational expectations, and family pressures. In English & Urdu."],
  ["03", "Individual Therapy", "Structured support for anxiety, trauma, burnout, identity, and life transitions."],
  ["04", "Children, Teens & Families", "Play therapy, emotional regulation, ADHD and autism support, and parent coaching."],
];

const Focus = () => (
  <section className="max-w-6xl mx-auto px-6 py-16 md:py-24">
    <Reveal><p className="cl-label">Areas of focus</p></Reveal>
    <Reveal i={1}><h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight max-w-2xl">Care that is structured, measured, and culturally informed.</h2></Reveal>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
      {FOCUS.map(([n, t, d], i) => (
        <Reveal key={n} i={i + 1} className="cl-card p-6">
          <p className="text-xs font-semibold text-[#6e4550]">{n}</p>
          <h3 className="mt-3 font-semibold leading-snug">{t}</h3>
          <p className="mt-2 text-sm text-[#2c1f20]/65 leading-relaxed">{d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const SERVICES = [
  "Individual Therapy", "Child Therapy", "Teen Therapy", "Family Counselling",
  "Couples & Marriage Counselling", "South Asian Couples Therapy", "Women's Issues",
  "Pre-natal / Postpartum", "Divorce / Separation", "Toxic Family Dynamics",
  "Parent Support & Coaching", "Menopause & Perimenopause Psychotherapy",
];

const Services = () => (
  <section id="cl-services" data-testid="cl-services" className="bg-[#f1ece4] border-y border-[#e5dcd3]">
    <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
      <Reveal><p className="cl-label">Services</p></Reveal>
      <Reveal i={1}><h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight">A complete range of therapy services.</h2></Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        {SERVICES.map((s, i) => (
          <a key={s} href={JANE_URL} target="_blank" rel="noreferrer" data-testid={`cl-service-${i + 1}`}
            className="group cl-card p-6 flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold text-[#6e4550]">{String(i + 1).padStart(2, "0")}</p>
              <p className="mt-2 font-medium leading-snug">{s}</p>
            </div>
            <ArrowUpRight size={16} className="mt-1 shrink-0 text-[#2c1f20]/30 group-hover:text-[#6e4550] transition-colors" />
          </a>
        ))}
      </div>
    </div>
  </section>
);

const LOCATIONS = [
  ["Milton", "Halton Medix Walk-In Clinic", "100 Bronte St S, Suite 2, Milton, ON", "Serving Milton, Campbellville, and the surrounding Halton region."],
  ["Burlington", "Halton Medix Walk-In Clinic", "3061 Walkers Line, Units 4 & 5, Burlington, ON", "Minutes from Oakville, Waterdown, and Hamilton."],
];

const Locations = () => (
  <section id="cl-locations" data-testid="cl-locations" className="max-w-6xl mx-auto px-6 py-16 md:py-24">
    <Reveal><p className="cl-label">In-person sessions</p></Reveal>
    <Reveal i={1}><h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight">Two clinics. One standard of care.</h2></Reveal>
    <div className="grid md:grid-cols-2 gap-4 mt-10">
      {LOCATIONS.map(([city, clinic, addr, note], i) => (
        <Reveal key={city} i={i + 1} className="cl-card p-7">
          <div className="flex items-center gap-2 text-[#6e4550]"><MapPin size={16} /><p className="text-xs font-semibold tracking-[0.15em] uppercase">{city}</p></div>
          <h3 className="mt-3 text-xl font-semibold">{clinic}</h3>
          <p className="mt-1 text-sm text-[#2c1f20]/60">{addr}</p>
          <p className="mt-3 text-sm text-[#2c1f20]/65 leading-relaxed">{note}</p>
          <a data-testid={`cl-book-${city.toLowerCase()}`} href={JANE_URL} target="_blank" rel="noreferrer" className="cl-btn cl-btn-outline mt-5 text-sm">Book in {city} <ArrowRight size={14} /></a>
        </Reveal>
      ))}
    </div>
  </section>
);

const CREDENTIALS = [
  ["Education", ["MA Counselling Psychology — Yorkville University, 2024", "Honours BASc, Human Behaviour — McMaster University", "BA, Psychology — McMaster University, 2021"]],
  ["Training & certifications", ["Gottman Method Couples Therapy", "Cognitive Processing Therapy (CPT)", "Play Therapy", "Applied Behaviour Analysis (ABA)", "Gender-Based Violence Training", "LivingWorks safeTALK"]],
  ["Experience", ["5+ years with children, teens & families, including home-based work", "1 year at a rape crisis centre — sexual violence, domestic abuse & PTSD"]],
  ["Registration & languages", ["Registered Psychotherapist (Qualifying) — CRPO", "Sessions in English & Urdu", "Virtual across Ontario · In-person in Milton & Burlington"]],
];

const About = () => (
  <section id="cl-about" data-testid="cl-about" className="bg-[#f1ece4] border-y border-[#e5dcd3]">
    <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
      <Reveal><p className="cl-label">About your therapist</p></Reveal>
      <div className="grid md:grid-cols-12 gap-10 mt-3 items-start">
        <div className="md:col-span-7">
          <Reveal i={1}><h2 className="text-3xl md:text-4xl font-semibold tracking-tight">Momna Sajid, RP (Qualifying)</h2></Reveal>
          <Reveal i={2}>
            <div className="mt-5 space-y-4 text-[#2c1f20]/70 leading-relaxed">
              <p>I offer a grounded space for reflection, clarity, and growth — supporting individuals, couples, and families through anxiety, stress, relationship challenges, identity concerns, and life transitions, with a particular focus on South Asian couples and families.</p>
              <p>My path to this work began with children and families: more than five years of support work, beginning as an ABA therapist working one-to-one with autistic children. That discipline still shapes my practice — therapy follows a plan we build together, and if something isn't working, we change it.</p>
              <p>With couples, I draw on the Gottman Method. Many of the couples I see are navigating in-law involvement, extended family expectations, and community pressures neither partner chose — so we get specific, and we practise the hard conversations in session, in English or Urdu.</p>
              <p>I also bring deep trauma experience, including a year at a rape crisis centre, with formal training in Cognitive Processing Therapy.</p>
            </div>
          </Reveal>
          <Reveal i={3}>
            <p className="cl-serif italic text-lg text-[#2c1f20] mt-6">"Your story is honoured here. Your pace is respected."</p>
          </Reveal>
        </div>
        <div className="md:col-span-5 grid sm:grid-cols-2 gap-4">
          {CREDENTIALS.map(([t, items], i) => (
            <Reveal key={t} i={i + 1} className="cl-card p-5">
              <p className="text-xs font-semibold tracking-[0.12em] uppercase text-[#2c1f20]">{t}</p>
              <ul className="mt-3 space-y-2">
                {items.map((it) => <li key={it} className="text-xs text-[#2c1f20]/65 leading-relaxed flex gap-2"><Check size={12} className="mt-0.5 shrink-0 text-[#6e4550]" />{it}</li>)}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const FAQS = [
  ["Where do sessions take place?", "Virtually across Ontario, or in person at Halton Medix Walk-In Clinic in Milton (100 Bronte St S, Suite 2) and Burlington (3061 Walkers Line, Units 4 & 5). Virtual sessions run on a secure, PHIPA-compliant video platform."],
  ["Do you offer therapy in Urdu?", "Yes. Sessions are available in English and Urdu, and you can move between languages whenever it helps."],
  ["Do you work with South Asian couples?", "Yes — this is a particular focus of the practice, including in-law dynamics, intergenerational expectations, and family approval, using Gottman Method-informed care."],
  ["Are receipts provided for insurance?", "Yes. A detailed receipt is provided after each session for submission to your insurer."],
  ["What is the cancellation policy?", "Sessions can be cancelled or rescheduled with 48 hours' notice at no charge."],
];

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="cl-faq" data-testid="cl-faq" className="max-w-3xl mx-auto px-6 py-16 md:py-24">
      <Reveal><p className="cl-label">FAQ</p></Reveal>
      <Reveal i={1}><h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight">Common questions.</h2></Reveal>
      <div className="mt-8 border border-[#e5dcd3] rounded-lg overflow-hidden">
        {FAQS.map(([q, a], i) => (
          <div key={q} className="border-b border-[#e5dcd3] last:border-b-0">
            <button data-testid={`cl-faq-${i + 1}`} onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex items-center justify-between px-6 py-5 text-left font-medium hover:bg-[#f1ece4] transition-colors">
              {q}
              <span className={`text-[#6e4550] text-xl transition-transform ${open === i ? "rotate-45" : ""}`}>+</span>
            </button>
            {open === i && <p className="px-6 pb-6 text-sm text-[#2c1f20]/65 leading-relaxed">{a}</p>}
          </div>
        ))}
      </div>
    </section>
  );
};

const Cta = () => (
  <footer id="cl-contact" data-testid="cl-contact" className="bg-[#2c1f20] text-white">
    <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
      <Reveal><p className="cl-label" style={{ color: "#d9b8ae" }}>Get started</p></Reveal>
      <Reveal i={1}><h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight max-w-2xl">Book your complimentary 15-minute consultation.</h2></Reveal>
      <Reveal i={2} className="mt-8 flex flex-wrap items-center gap-6">
        <a data-testid="cl-footer-book" href={JANE_URL} target="_blank" rel="noreferrer" className="cl-btn bg-white text-[#2c1f20] hover:bg-[#d9b8ae]">
          <HeartHandshake size={16} /> Book via Jane App
        </a>
        <span className="flex flex-wrap gap-6 text-sm text-white/70">
          <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-2 hover:text-white transition-colors"><Mail size={14} /> momna@hayahealingpsychotherapy.com</a>
          <a href="tel:+13656172135" className="flex items-center gap-2 hover:text-white transition-colors"><Phone size={14} /> (365) 617-2135</a>
        </span>
      </Reveal>
      <div className="mt-14 pt-6 border-t border-white/15 text-xs text-white/45 flex flex-wrap justify-between gap-2">
        <span>Haya Healing Psychotherapy · Milton & Burlington · Virtual across Ontario</span>
        <span>Clinical/corporate design sample</span>
      </div>
    </div>
  </footer>
);

export default function Clinical() {
  useEffect(() => {
    document.title = "Evidence-Based Psychotherapy in Milton & Burlington | Haya Healing Psychotherapy";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Structured, evidence-based psychotherapy for individuals, couples, and families in Milton & Burlington and virtually across Ontario. Complimentary 15-minute consultation.");
  }, []);
  return (
    <div className="cl-root min-h-screen">
      <Banner />
      <TopBar />
      <Nav />
      <Hero />
      <TrustBar />
      <Focus />
      <Services />
      <Locations />
      <About />
      <Faq />
      <Cta />
    </div>
  );
}
